// Promise polyfill
import "core-js/features/promise";
import { render } from "react-dom";
import { BrowserRouter } from "react-router-dom";
import axios, { AxiosError, AxiosRequestConfig } from "axios";
import App from "./components/App";
import "@amzn/awsui-global-styles/global-styles.css";
import "./globalStyles.scss";
import { getMidwayJwtToken } from "./auth/MidwayJwtToken";
import {
  canResendAfterSignIn,
  establishMidwayAuthorizerSession,
  isLuscaApiUrl,
} from "./auth/midwayAuthorizerSession";
import {
  getAppSetting,
  IAppSettings,
  initializeAppSettings,
} from "./config/AppSettings";
import { applyLuscaTheme } from "./config/theme";
import { withDevUser } from "./helpers/devUser";

applyLuscaTheme();

// API requests are authenticated by MidwayAuthorizer's session cookie, not a bearer token.
axios.interceptors.request.use((config) => {
  const apiHost = getAppSetting("apiUrl") as string | undefined;
  // eslint-disable-next-line no-param-reassign
  config.url = withDevUser(config.url, apiHost, window.location.search);
  // Sends the MidwayAuthorizer session cookie; S3 presigned URLs stay uncredentialed.
  if (isLuscaApiUrl(config.url, apiHost)) {
    // eslint-disable-next-line no-param-reassign
    config.withCredentials = true;
  }
  return config;
});

// An API request that fails with no response may be MidwayAuthorizer redirecting to
// Midway: sign in to it and retry the request once, if it is safe to repeat.
type RetriableConfig = AxiosRequestConfig & {
  luscaSessionRetried?: boolean;
};
axios.interceptors.response.use(undefined, async (error: unknown) => {
  const axiosError = error as AxiosError | undefined;
  const config = axiosError?.config as RetriableConfig | undefined;
  const apiHost = getAppSetting("apiUrl") as string | undefined;
  if (
    !axios.isCancel(error) &&
    axiosError?.response === undefined &&
    config &&
    !config.luscaSessionRetried &&
    canResendAfterSignIn(
      config.method,
      config.headers as Record<string, unknown> | undefined,
      axiosError?.code
    ) &&
    apiHost &&
    isLuscaApiUrl(config.url, apiHost)
  ) {
    config.luscaSessionRetried = true;
    if (await establishMidwayAuthorizerSession(apiHost)) {
      return axios(config);
    }
  }
  return Promise.reject(error);
});

(async () => {
  // Sign in to Midway before rendering; the user's alias comes from the Midway identity token
  await getMidwayJwtToken();

  // Initialize application settings
  const appSettings = (await axios("/settings.json")).data as IAppSettings;
  initializeAppSettings(appSettings);

  render(
    <BrowserRouter>
      <App />
    </BrowserRouter>,
    document.querySelector("#app")
  );
})().catch((error) => console.log(error));
